import {
  Button,
  Col,
  Form,
  InputNumber,
  List,
  Row,
  Select,
  Space,
  theme,
  Tooltip,
  Typography,
} from "antd";
import { useEffect, useRef, useState } from "react";
import Icon from "@ant-design/icons";
import { modalTitle } from "../../controls/InModal.default";
import ModalFooter from "../../controls/ModalFooter";
import ModalDeleteConfirmation from "../../controls/ModalDeleteConfirmation";
import ModalErrorAlert from "../../controls/ModalErrorAlert";
import BandListEditor, {
  BandListEditorHandle,
  BandRow,
  toBandRows,
} from "../../controls/BandListEditor";
import FieldInfo from "../../controls/FieldInfo";
import _ from "lodash";
import DynamicForm from "../../controls/DynamicForm";
import InModal from "../../controls/InModal";
import { Icons } from "../../assets/icons";
import { InputObject } from "../../controls/default";
import { retry, getProblemMessage } from "../../utils/http";
import { nameOf } from "../../utils/typescript";
import { QueryOperation } from "../../code/search-query/search-query";
import {
  addAssessmentReport,
  updateAssessmentReport,
  deleteAssessmentReport,
  getReportKpiDefinitions,
  addReportKpiDefinition,
  deleteReportKpiDefinition,
  getKpis,
  getMetricDefinitions,
  getAssessmentReports,
  getReportComponents,
  addReportComponent,
  deleteReportComponent,
  getReportBands,
  addReportBand,
  updateReportBand,
  deleteReportBand,
} from "../../repository/data.repository";
import {
  AssessmentReport,
  Kpi,
  MAX_PAGE_SIZE,
  ReportComponentInputType,
  ReportComponentRole,
} from "../../repository/data.default";

// Names of a Meta report's component inputs, by uid. Failures leave the uid showing rather than blocking the dialog.
async function resolveComponentNames(components: any[]): Promise<Record<string, string>> {
  const uidsOf = (key: string) => components.map((c) => c[key]).filter(Boolean) as string[];
  const byUids = (uids: string[]) => ({
    searchQuery: { conditions: [{ field: "uid", operation: QueryOperation.In, values: uids }] },
    pageNumber: 0,
    pageSize: uids.length,
  });
  const lookups: Promise<{ data: { uid: string; name: string }[] }>[] = [];
  const kpis = uidsOf("inputKpiDefinitionUid");
  const metrics = uidsOf("inputMetricDefinitionUid");
  const reports = uidsOf("inputReportUid");
  if (kpis.length) lookups.push(getKpis(byUids(kpis)) as any);
  if (metrics.length) lookups.push(getMetricDefinitions(byUids(metrics)) as any);
  if (reports.length) lookups.push(getAssessmentReports(byUids(reports)) as any);
  const results = await Promise.allSettled(lookups);
  const names: Record<string, string> = {};
  for (const r of results)
    if (r.status === "fulfilled") for (const item of r.value.data) names[item.uid] = item.name;
  return names;
}

export interface AssessmentReportFormDialogProps {
  readonly open?: boolean;
  readonly mode: "add" | "update" | "delete";
  readonly initialValues?: AssessmentReport;
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
}

interface AssessmentReportForm {
  readonly name: string;
  readonly shortDescription: string;
  readonly description: string;
}

interface ComponentItem {
  readonly key: string;
  __type: "add" | "existing" | "delete";
  junctionUid?: string;
  inputType: ReportComponentInputType;
  display: string;
  inputKpiDefinitionUid?: string;
  inputMetricDefinitionUid?: string;
  inputReportUid?: string;
  weight: number;
  role: ReportComponentRole;
  order: number;
}

function AssessmentReportFormDialog(props: AssessmentReportFormDialogProps) {
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const values = Form.useWatch<AssessmentReportForm>([], form);
  const [valid, setValid] = useState(false);
  const [loading, setLoading] = useState(false);
  // Gates the form on open while KPIs/components load (separate from the submit `loading`).
  const [dataLoading, setDataLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [kind, setKind] = useState<"Base" | "Meta">("Base");
  const [operator, setOperator] = useState<"Gap" | "WeightedIndex">(
    "WeightedIndex",
  );

  // Base: KPI picker
  const [kpisForm] = Form.useForm();
  const kpisValues = Form.useWatch<{ readonly kpi: InputObject }>([], kpisForm);
  const [kpis, setKpis] = useState<InputObject[]>([]);

  // Meta: components builder
  const [components, setComponents] = useState<ComponentItem[]>([]);
  const [compInputType, setCompInputType] =
    useState<ReportComponentInputType>("Kpi");
  const [compRole, setCompRole] = useState<ReportComponentRole>("Term");
  const [compWeight, setCompWeight] = useState<number>(1);
  const [compEntity, setCompEntity] = useState<{
    value: string;
    label: string;
  }>();
  const [entityOptions, setEntityOptions] = useState<
    { label: string; value: string }[]
  >([]);

  // Bands (verdict thresholds + guidance) — apply to every report kind; flushed via the editor ref on save.
  const [bandRows, setBandRows] = useState<BandRow[]>([]);
  const bandsEditorRef = useRef<BandListEditorHandle>(null);

  function onError(e: unknown): void {
    setLoading(false);
    setErrorMessage(getProblemMessage(e, "Something went wrong, try again"));
  }

  useEffect(() => {
    if (!props.open) {
      reset();
      return;
    }
    form.setFieldsValue(props.initialValues || {});
    kpisForm.resetFields();
    const k = (props.initialValues?.kind as "Base" | "Meta") || "Base";
    setKind(k);
    setOperator(
      (props.initialValues?.compositeOperator as "Gap" | "WeightedIndex") ||
        "WeightedIndex",
    );

    if (props.initialValues?.uid) {
      const uid = props.initialValues.uid;
      const byReport = {
        searchQuery: {
          conditions: [
            {
              field: "reportUid",
              operation: QueryOperation.Equal,
              values: [uid],
            },
          ],
        },
        pageNumber: 0,
        pageSize: MAX_PAGE_SIZE,
      };
      // Children differ by kind (Base = KPI bundle, Meta = components); bands apply to every report.
      const childrenPromise: Promise<any> =
        k === "Base"
          ? retry(() => getReportKpiDefinitions(byReport))
          : retry(() => getReportComponents(byReport));

      setDataLoading(true);
      Promise.all([childrenPromise, retry(() => getReportBands(byReport))])
        .then(([childrenData, bandsData]: any) => {
          if (k === "Base") {
            setKpis(
              childrenData.data.map((j: any) => ({
                display: j.kpiDefinitionName,
                value: j.kpiDefinitionUid,
                dataItem: { __type: "update", junctionUid: j.uid },
              })),
            );
            setComponents([]);
          } else {
            setComponents(
              [...childrenData.data]
                .sort((a: any, b: any) => a.order - b.order)
                .map((c: any) => ({
                  key: c.uid,
                  __type: "existing" as const,
                  junctionUid: c.uid,
                  inputType: c.inputType,
                  display:
                    c.inputKpiDefinitionUid ||
                    c.inputMetricDefinitionUid ||
                    c.inputReportUid ||
                    "(input)",
                  inputKpiDefinitionUid: c.inputKpiDefinitionUid ?? undefined,
                  inputMetricDefinitionUid:
                    c.inputMetricDefinitionUid ?? undefined,
                  inputReportUid: c.inputReportUid ?? undefined,
                  weight: c.weight,
                  role: c.role,
                  order: c.order,
                })),
            );
            // A component row carries only its input's uid; resolve the names (one query per input kind) so
            // an existing component reads "Readiness - Overall Index", not a raw id.
            resolveComponentNames(childrenData.data).then((names) =>
              setComponents((prev) =>
                prev.map((row: any) => {
                  const uid = row.inputKpiDefinitionUid || row.inputMetricDefinitionUid || row.inputReportUid;
                  return uid && names[uid] ? { ...row, display: names[uid] } : row;
                }),
              ),
            );
            setKpis([]);
          }
          setBandRows(toBandRows(bandsData.data, (b: any) => b.upperThreshold));
        })
        .catch(() => {})
        .finally(() => setDataLoading(false));
    } else {
      setKpis([]);
      setComponents([]);
      setBandRows([]);
    }
  }, [props.open]);

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then((value) => setValid(!!Object.values(value).length))
      .catch(() => setValid(false));
  }, [form, values, props.open]);

  const kpisRequest = (search: string) =>
    retry(() =>
      getKpis({
        searchQuery: {
          conditions: [
            {
              field: nameOf<Kpi>("name"),
              operation: QueryOperation.StartsWith,
              values: [search],
            },
          ],
        },
        pageNumber: 0,
        pageSize: 100,
      }),
    ).then(({ data }) =>
      data.map((dataItem) => ({
        display: dataItem.name,
        value: dataItem.uid,
        dataItem,
      })),
    );

  function onAddKpi(): void {
    const { kpi } = kpisValues || {};
    if (kpi != null) {
      const next = _.uniqBy(
        [
          ...kpis.filter((item) => item.value !== kpi.value),
          { ...kpi, dataItem: { ...kpi.dataItem, __type: "add" } },
        ],
        (item) => item.value,
      );
      setKpis(next);
      kpisForm.resetFields();
    }
  }

  function onRemoveKpi(value: string): void {
    setKpis(
      kpis.map((item) =>
        item.value === value
          ? { ...item, dataItem: { ...item.dataItem, __type: "delete" } }
          : item,
      ),
    );
  }

  function addOrDeleteKpis(reportUid: string) {
    const adds = kpis.filter(({ dataItem }) => dataItem.__type === "add");
    const dels = kpis.filter(
      ({ dataItem }) => dataItem.__type === "delete" && dataItem.junctionUid,
    );
    const promises: Promise<unknown>[] = [];
    dels.forEach(({ dataItem }) =>
      promises.push(deleteReportKpiDefinition(dataItem.junctionUid)),
    );
    adds.forEach(({ value }) =>
      promises.push(addReportKpiDefinition({ reportUid, kpiDefinitionUid: value })),
    );
    return Promise.all(promises);
  }

  // ----- Meta component builder -----
  function searchEntities(search: string): void {
    const q = {
      searchQuery: {
        conditions: search
          ? [
              {
                field: "name",
                operation: QueryOperation.StartsWith,
                values: [search],
              },
            ]
          : [],
      },
      pageNumber: 0,
      pageSize: 100,
    };
    const p: Promise<any> =
      compInputType === "Kpi"
        ? getKpis(q)
        : compInputType === "Metric"
          ? getMetricDefinitions(q)
          : getAssessmentReports(q);
    retry(() => p)
      .then(({ data }: any) =>
        setEntityOptions(
          (data as any[]).map((d) => ({ label: d.name, value: d.uid })),
        ),
      )
      .catch(() => setEntityOptions([]));
  }

  function onAddComponent(): void {
    if (!compEntity) return;
    const item: ComponentItem = {
      key: `${compInputType}:${compEntity.value}:${components.length}`,
      __type: "add",
      inputType: compInputType,
      display: compEntity.label,
      weight: compWeight,
      role: operator === "Gap" ? compRole : "Term",
      order: components.length,
      inputKpiDefinitionUid:
        compInputType === "Kpi" ? compEntity.value : undefined,
      inputMetricDefinitionUid:
        compInputType === "Metric" ? compEntity.value : undefined,
      inputReportUid: compInputType === "Report" ? compEntity.value : undefined,
    };
    setComponents([...components, item]);
    setCompEntity(undefined);
    setEntityOptions([]);
  }

  function onRemoveComponent(key: string): void {
    setComponents(
      components
        .map((c) =>
          c.key === key
            ? c.__type === "add"
              ? null
              : { ...c, __type: "delete" as const }
            : c,
        )
        .filter((c): c is ComponentItem => c != null),
    );
  }

  function persistComponents(reportUid: string) {
    const adds = components.filter((c) => c.__type === "add");
    const dels = components.filter(
      (c) => c.__type === "delete" && c.junctionUid,
    );
    const promises: Promise<unknown>[] = [];
    dels.forEach((c) => promises.push(deleteReportComponent(c.junctionUid!)));
    adds.forEach((c, i) =>
      promises.push(
        addReportComponent({
          reportUid,
          inputType: c.inputType,
          inputKpiDefinitionUid: c.inputKpiDefinitionUid ?? null,
          inputMetricDefinitionUid: c.inputMetricDefinitionUid ?? null,
          inputReportUid: c.inputReportUid ?? null,
          weight: c.weight,
          role: c.role,
          normalize: false,
          order: c.order ?? i,
        }),
      ),
    );
    return Promise.all(promises);
  }

  function persistChildren(reportUid: string): Promise<unknown> {
    const composition =
      kind === "Meta"
        ? persistComponents(reportUid)
        : addOrDeleteKpis(reportUid);
    const bands = bandsEditorRef.current?.persist(reportUid) ?? Promise.resolve();
    return Promise.all([composition, bands]);
  }

  function onSubmit(): void {
    const { name, shortDescription, description } = values;
    setLoading(true);
    setErrorMessage(null);

    const base = {
      name,
      shortDescription,
      description,
      kind,
      compositeOperator: kind === "Meta" ? operator : null,
    };

    if (props.mode === "add") {
      addAssessmentReport(base)
        .then(({ uid }) =>
          persistChildren(uid).then(() => {
            setLoading(false);
            props.onFinish?.();
          }),
        )
        .catch(onError);
    } else if (props.mode === "update") {
      updateAssessmentReport({
        uid: props.initialValues?.uid,
        rowVersion: props.initialValues?.rowVersion,
        ...base,
      })
        .then(() =>
          persistChildren(props.initialValues?.uid!).then(() => {
            setLoading(false);
            props.onFinish?.();
          }),
        )
        .catch(onError);
    } else if (props.mode === "delete") {
      deleteAssessmentReport(props.initialValues?.uid!)
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch(onError);
    }
  }

  function reset(): void {
    setLoading(false);
    setValid(false);
    setErrorMessage(null);
    setKpis([]);
    setComponents([]);
    setBandRows([]);
    setKind("Base");
    setOperator("WeightedIndex");
    setCompEntity(undefined);
    setEntityOptions([]);
    form.resetFields();
    kpisForm.resetFields();
  }

  const visibleComponents = components.filter((c) => c.__type !== "delete");
  const visibleKpis = kpis.filter(({ dataItem }) => dataItem.__type !== "delete");

  return (
    <InModal
      open={props.open}
      loading={dataLoading}
      loadingLabel="Loading report…"
      size={props.mode === "delete" ? "small" : "large"}
      title={modalTitle(props.mode, "Report", props.initialValues?.name)}
      onCancel={props.onCancel}
      actions={
        <ModalFooter
          mode={props.mode}
          loading={loading}
          valid={valid}
          onCancel={props.onCancel}
          onSubmit={onSubmit}
        />
      }
    >
      {props.mode !== "delete" && (
        <Row className="mb-4 flex flex-row" gutter={16}>
          {/* Report form */}
          <Col className="flex flex-1 flex-col">
            <DynamicForm
              className="w-auto"
              form={form}
              schema={{
                groups: [
                  {
                    controls: [
                      {
                        type: "textbox",
                        name: "name",
                        label: "Name",
                        rules: [{ required: true, message: "Name is required" }],
                      },
                      {
                        type: "textbox",
                        name: "shortDescription",
                        label: "Short description",
                        rules: [
                          { required: true, message: "Short description is required" },
                        ],
                      },
                      {
                        type: "areabox",
                        name: "description",
                        label: "Description",
                        rows: 3,
                        rules: [
                          { required: true, message: "Description is required" },
                        ],
                      },
                    ],
                  },
                ],
              }}
            />
            <Space direction="vertical" className="mt-2 w-full">
              <Typography.Text type="secondary">
                Report type
                <FieldInfo title="Base = a straight bundle of KPIs (its value is the mean of their domain scores). Meta = a value computed from other inputs (KPIs, metrics or reports) via an operator." />
              </Typography.Text>
              <Select
                className="w-full"
                value={kind}
                onChange={(v) => setKind(v)}
                options={[
                  { label: "Base — bundle of KPIs", value: "Base" },
                  { label: "Meta — computed composite", value: "Meta" },
                ]}
              />
              {kind === "Meta" && (
                <>
                  <Typography.Text type="secondary">
                    Composite operator
                    <FieldInfo title="Weighted Index = Σ (weight × input) — a single blended score. Gap = |A − B| per shared domain, comparing a Minuend input (A) against a Subtrahend input (B)." />
                  </Typography.Text>
                  <Select
                    className="w-full"
                    value={operator}
                    onChange={(v) => setOperator(v)}
                    options={[
                      { label: "Weighted Index (Σ wᵢ·inputᵢ)", value: "WeightedIndex" },
                      { label: "Gap (|A − B| by domain)", value: "Gap" },
                    ]}
                  />
                </>
              )}
            </Space>
          </Col>

          {/* Composition: KPIs (Base) or components (Meta) */}
          <Col className="flex flex-1 flex-col">
            {kind === "Base" ? (
              <>
                <DynamicForm
                  className="w-full"
                  form={kpisForm}
                  schema={{
                    groups: [
                      {
                        controls: [
                          {
                            type: "combobox",
                            label: "KPIs",
                            name: "kpi",
                            request: kpisRequest,
                          },
                        ],
                      },
                    ],
                  }}
                  onChange={onAddKpi}
                />
                {!!visibleKpis.length && (
                  <List
                    className="ml-1 mr-1"
                    style={{
                      backgroundColor: token.colorBgLayout,
                      borderRadius: token.borderRadius,
                    }}
                    dataSource={visibleKpis}
                    renderItem={(item) => (
                      <List.Item className="p-1 pl-2 pr-2">
                        <Col className="flex flex-auto flex-col">
                          <Typography.Text>{item.display}</Typography.Text>
                        </Col>
                        <Button
                          type="text"
                          icon={
                            <Icon
                              className="-ml-0.5 text-xl"
                              component={Icons.CloseIcon}
                            />
                          }
                          onClick={() => onRemoveKpi(item.value)}
                        />
                      </List.Item>
                    )}
                  />
                )}
              </>
            ) : (
              <>
                <Typography.Text type="secondary">
                  Components
                  <FieldInfo title="The inputs this Meta report combines. Each is a KPI, Metric or another Report, with a weight (and, for Gap, a role) — added one at a time below." />
                </Typography.Text>
                <Space.Compact className="mt-1 w-full" block>
                  <Select
                    style={{ width: 110 }}
                    value={compInputType}
                    onChange={(v) => {
                      setCompInputType(v);
                      setCompEntity(undefined);
                      setEntityOptions([]);
                    }}
                    options={[
                      { label: "KPI", value: "Kpi" },
                      { label: "Metric", value: "Metric" },
                      { label: "Report", value: "Report" },
                    ]}
                  />
                  <Select
                    style={{ flex: 1 }}
                    showSearch
                    filterOption={false}
                    placeholder="Search…"
                    value={compEntity?.value}
                    options={entityOptions}
                    onSearch={searchEntities}
                    onFocus={() => searchEntities("")}
                    onChange={(value, opt: any) =>
                      setCompEntity({ value, label: opt?.label })
                    }
                  />
                </Space.Compact>
                <Space className="mt-1" wrap>
                  <Tooltip title="Weight — the multiplier applied to this input in the Weighted Index (Σ weight × input). Ignored for Gap reports.">
                    <InputNumber
                      style={{ width: 90 }}
                      value={compWeight}
                      step={0.05}
                      onChange={(v) => setCompWeight(Number(v ?? 0))}
                      addonBefore="w"
                    />
                  </Tooltip>
                  {operator === "Gap" && (
                    <Tooltip title="Role in the gap formula |A − B|: Minuend is A (e.g. managers), Subtrahend is B (e.g. employees). Pick exactly one of each.">
                      <Select
                        style={{ width: 140 }}
                        value={compRole}
                        onChange={(v) => setCompRole(v)}
                        options={[
                          { label: "Minuend (A)", value: "Minuend" },
                          { label: "Subtrahend (B)", value: "Subtrahend" },
                        ]}
                      />
                    </Tooltip>
                  )}
                  <Button onClick={onAddComponent} disabled={!compEntity}>
                    Add
                  </Button>
                </Space>
                {!!visibleComponents.length && (
                  <List
                    className="ml-1 mr-1 mt-2"
                    style={{
                      backgroundColor: token.colorBgLayout,
                      borderRadius: token.borderRadius,
                    }}
                    dataSource={visibleComponents}
                    renderItem={(item) => (
                      <List.Item className="p-1 pl-2 pr-2">
                        <Col className="flex flex-auto flex-col">
                          <Typography.Text>
                            [{item.inputType}] {item.display}
                          </Typography.Text>
                          <Typography.Text type="secondary">
                            weight {item.weight}
                            {operator === "Gap" ? ` · ${item.role}` : ""}
                          </Typography.Text>
                        </Col>
                        <Button
                          type="text"
                          icon={
                            <Icon
                              className="-ml-0.5 text-xl"
                              component={Icons.CloseIcon}
                            />
                          }
                          onClick={() => onRemoveComponent(item.key)}
                        />
                      </List.Item>
                    )}
                  />
                )}
              </>
            )}
          </Col>
        </Row>
      )}

      {props.mode !== "delete" && (
        <Space direction="vertical" className="w-full">
          <Typography.Text type="secondary">
            Bands — verdict thresholds + guidance (interpretation &amp; action,
            surfaced in the exported PDF)
          </Typography.Text>
          <BandListEditor
            ref={bandsEditorRef}
            mode={props.mode}
            ownerKeyName="reportUid"
            thresholdKeyName="upperThreshold"
            initial={bandRows}
            columns={[
              { key: "label", label: "Label", type: "text" },
              { key: "threshold", label: "Upper threshold", type: "number" },
              { key: "color", label: "Color", type: "color" },
              {
                key: "interpretation",
                label: "Interpretation",
                type: "textarea",
              },
              {
                key: "recommendedAction",
                label: "Recommended action",
                type: "textarea",
              },
            ]}
            onAdd={addReportBand}
            onUpdate={updateReportBand}
            onDelete={deleteReportBand}
          />
        </Space>
      )}

      {props.mode === "delete" && (
        <ModalDeleteConfirmation
          entityLabel="report"
          entityName={props.initialValues?.name}
        />
      )}

      <ModalErrorAlert message={errorMessage} />
    </InModal>
  );
}

export default AssessmentReportFormDialog;
